import { useEffect, useRef, useState } from 'react';
import {
  BAR, COUPLE, DANCE_FLOOR, ENTRANCES, ROOM, ROUND_R, TABLES, centreOf, tableFor, type SeatTable,
} from '../data/seating-plan';
import { IconClose } from './Icon';

/**
 * The reception's tables, in the resort plan's colours (D73).
 *
 * Tables are drawn the way the plan draws buildings: terracotta with a thin
 * line. The guest's own table is the plan's other colour, the olive that
 * means "you" on the map, because the site's accent is a terracotta too and a
 * highlight in it would barely show against the rest.
 *
 * `compact` is the card on Now: the whole room in a phone's width, which leaves
 * a table's name about four pixels tall, so it shows numbers only. The names
 * come back in the expanded view.
 */
export function SeatingSvg({ mine, compact = false }: { mine: SeatTable | 'couple' | null; compact?: boolean }) {
  const cls = (t: boolean) => (t ? 'seat-table is-mine' : 'seat-table');
  return (
    <svg
      className={compact ? 'seat-plan is-compact' : 'seat-plan'}
      viewBox={`0 0 ${ROOM.w} ${ROOM.h}`}
      role="img"
      aria-label={mine === 'couple' ? "Reception seating plan, the couple's table marked"
        : mine ? `Reception seating plan, table ${mine.no} marked` : 'Reception seating plan'}
    >
      <rect width={ROOM.w} height={ROOM.h} className="seat-floor" />
      <path d={`M ${ROOM.w - BAR.r} 0 A ${BAR.r} ${BAR.r} 0 0 0 ${ROOM.w} ${BAR.r} L ${ROOM.w} 0 Z`} className="seat-area" />
      <text x={ROOM.w - BAR.r * 0.4} y={BAR.r * 0.4} dy="0.35em" textAnchor="middle" className="seat-legend">Bar</text>

      <Box {...DANCE_FLOOR} className="seat-area" />
      {['Dance', 'floor'].map((word, i) => (
        <text key={word} x={DANCE_FLOOR.x + DANCE_FLOOR.w / 2} y={DANCE_FLOOR.y + DANCE_FLOOR.h / 2 + (i - 0.5) * 40} dy="0.35em"
          textAnchor="middle" className="seat-legend">{word}</text>
      ))}

      {ENTRANCES.map(({ y }) => (
        <g key={y} className="seat-door">
          <line x1={ROOM.w - 6} y1={y} x2={ROOM.w - 78} y2={y} />
          <polyline points={`${ROOM.w - 56},${y - 18} ${ROOM.w - 78},${y} ${ROOM.w - 56},${y + 18}`} />
          <text x={ROOM.w - 10} y={y - 30} textAnchor="end" className="seat-door-label">Entrance</text>
        </g>
      ))}

      {mine && (() => {
        const [cx, cy] = mine === 'couple' ? [COUPLE.x + COUPLE.w / 2, COUPLE.y + COUPLE.h / 2] : centreOf(mine);
        return <circle cx={cx} cy={cy} r={mine !== 'couple' && mine.shape === 'long' ? 200 : ROUND_R + 40} className="seat-halo" />;
      })()}

      <g>
        <rect x={COUPLE.x} y={COUPLE.y} width={COUPLE.w} height={COUPLE.h} rx={8} className={cls(mine === 'couple')} />
        <Label x={COUPLE.x + COUPLE.w / 2} y={COUPLE.y + COUPLE.h / 2} lines={compact ? ['♥'] : ['Couple']} />
      </g>

      {TABLES.map((t) => {
        const [cx, cy] = centreOf(t);
        return (
          <g key={t.no}>
            {t.shape === 'round'
              ? <circle cx={t.x} cy={t.y} r={ROUND_R} className={cls(mine === t)} />
              : <rect x={t.x} y={t.y} width={t.w} height={t.h} rx={10} className={cls(mine === t)} />}
            <Label x={cx} y={cy} no={t.no} lines={compact ? [] : t.lines} long={t.shape === 'long'} />
          </g>
        );
      })}
    </svg>
  );
}

/** The data's `w`/`h` as SVG's `width`/`height`. */
const Box = ({ x, y, w, h, className }: { x: number; y: number; w: number; h: number; className: string }) =>
  <rect x={x} y={y} width={w} height={h} rx={8} className={className} />;

/**
 * A table's number above its name, centred as a block. A long table is only
 * 70 units deep, so it puts the two side by side instead.
 */
function Label({ x, y, no, lines, long = false }: { x: number; y: number; no?: number; lines: string[]; long?: boolean }) {
  if (long && no !== undefined && lines.length) {
    return (
      <text x={x} y={y} dy="0.35em" textAnchor="middle" className="seat-name">
        <tspan className="seat-no">{no}</tspan>{'\u2002'}{lines.join(' ')}
      </text>
    );
  }
  const rows = [
    ...(no !== undefined ? [{ text: String(no), cls: 'seat-no', h: 44 }] : []),
    ...lines.map((text) => ({ text, cls: 'seat-name', h: 26 })),
  ];
  let top = y - rows.reduce((sum, r) => sum + r.h, 0) / 2;
  return (
    <>
      {rows.map((r) => {
        const mid = top + r.h / 2;
        top += r.h;
        return <text key={r.text} x={x} y={mid} dy="0.35em" textAnchor="middle" className={r.cls}>{r.text}</text>;
      })}
    </>
  );
}

/**
 * The seating plan on Now, while the reception is what is next (D73).
 *
 * A card with the whole room in miniature and the guest's table marked; a tap
 * opens it full screen with every table named. On a phone the full view is
 * wider than the screen, so it scrolls sideways and opens centred on the
 * guest's own table, which is the one they opened it to find.
 */
export default function SeatingCard({ table }: { table?: string }) {
  const [open, setOpen] = useState(false);
  const mine = tableFor(table);
  const where = mine === 'couple' ? "You're at the couple's table"
    : mine ? `You're at ${mine.no} · ${mine.name}` : null;

  return (
    <>
      <div className="day-head">
        <h4>Seating</h4>
        {where && <span className="seat-where">{where}</span>}
      </div>
      <div className="ev-list">
        <button type="button" className="card seat-card" onClick={() => setOpen(true)}
          aria-label="Open the seating plan">
          <SeatingSvg mine={mine} compact />
          <span className="seat-expand">Tap to expand</span>
        </button>
      </div>
      {open && <SeatingFull mine={mine} where={where} onClose={() => setOpen(false)} />}
    </>
  );
}

function SeatingFull({ mine, where, onClose }: {
  mine: SeatTable | 'couple' | null; where: string | null; onClose: () => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Centre the guest's table. Read off the rendered width rather than assumed,
  // since how much of the room fits depends on the screen.
  useEffect(() => {
    const el = scroller.current;
    if (!el || !mine) return;
    const [cx, cy] = mine === 'couple' ? [COUPLE.x + COUPLE.w / 2, COUPLE.y + COUPLE.h / 2] : centreOf(mine);
    const k = el.scrollWidth / ROOM.w;
    el.scrollLeft = cx * k - el.clientWidth / 2;
    el.scrollTop = cy * (el.scrollHeight / ROOM.h) - el.clientHeight / 2;
  }, [mine]);

  return (
    <div className="seat-full" role="dialog" aria-modal="true" aria-label="Reception seating plan">
      <div className="seat-full-head">
        <div>
          <h4 style={{ margin: 0 }}>Reception seating</h4>
          {where && <span className="seat-where">{where}</span>}
        </div>
        <button type="button" className="sheet-close" onClick={onClose} aria-label="Close" autoFocus>
          <IconClose size={16} />
        </button>
      </div>
      <div className="seat-full-scroll" ref={scroller}>
        <SeatingSvg mine={mine} />
      </div>
    </div>
  );
}
